/**
 * Progress Report API Service
 * Handles progress report related API calls
 */
import apiService from './apiService';

class ProgressReportService {
  constructor() {
    this.basePath = '/api/progressreport';
  }

  /**
   * Get test results by category
   * @param {Object} options - Filter options
   * @param {Date} [options.DateFrom] - Start date range for the report
   * @param {Date} [options.DateTo] - End date range for the report
   * @param {string} [options.ExamMode] - Exam mode to filter by (e.g., "Practice", "Simulated")
   * @param {number[]} [options.CategoryIds] - Category IDs to filter by
   * @param {number} [options.ExamTypeId] - Exam type ID to filter by
   * @param {number} [options.CategoryGroupId] - Category group ID to filter by
   * @returns {Promise<Array>} - Array of TestCategoryReportModel
   */
  async getTestByCategoryReport(options = {}) {
    try {
      const params = {};
      
      if (options.DateFrom) {
        params.DateFrom = options.DateFrom instanceof Date 
          ? options.DateFrom.toISOString() 
          : options.DateFrom;
      }
      
      if (options.DateTo) {
        params.DateTo = options.DateTo instanceof Date 
          ? options.DateTo.toISOString() 
          : options.DateTo;
      }
      
      if (options.ExamMode) {
        params.ExamMode = options.ExamMode;
      }
      
      if (options.CategoryIds && options.CategoryIds.length > 0) {
        params.CategoryIds = options.CategoryIds.join(',');
      }
      
      if (options.ExamTypeId) {
        params.ExamTypeId = options.ExamTypeId;
      }
      
      if (options.CategoryGroupId) {
        params.CategoryGroupId = options.CategoryGroupId;
      }

      return await apiService.get(`${this.basePath}/test-by-category`, params);
    } catch (error) {
      console.error('Failed to fetch progress report:', error);
      throw error;
    }
  }

  /**
   * Get test results by category for the last N days
   * @param {number} days - Number of days to look back
   * @param {number} [examTypeId] - Optional exam type ID filter
   * @returns {Promise<Array>} - Array of TestCategoryReportModel
   */
  async getTestByCategoryReportForLastDays(days, examTypeId = null) {
    const DateTo = new Date();
    const DateFrom = new Date();
    DateFrom.setDate(DateFrom.getDate() - days);

    const options = { DateFrom, DateTo };
    if (examTypeId) {
      options.ExamTypeId = examTypeId;
    }

    return this.getTestByCategoryReport(options);
  }

  /**
   * Get performance summary
   * @param {Object} options - Filter options
   * @param {number} [options.ExamTypeId] - Optional exam type ID to filter by
   * @param {string} [options.ExamMode] - Optional exam mode to filter by (e.g., "Practice", "Simulated")
   * @param {Date} [options.StartDateFrom] - Optional start date range from
   * @param {Date} [options.StartDateTo] - Optional start date range to
   * @param {number[]} [options.CategoryIds] - Optional category IDs to filter by
   * @returns {Promise<Object>} - PerformanceSummaryModel with additional metrics like examsThisWeek, bestDailyScore, averageDailyQuestions
   */
  async getPerformanceSummary(options = {}) {
    try {
      const params = {};
      
      if (options.ExamTypeId) {
        params.ExamTypeId = options.ExamTypeId;
      }
      
      if (options.ExamMode) {
        params.ExamMode = options.ExamMode;
      }
      
      if (options.StartDateFrom) {
        params.StartDateFrom = options.StartDateFrom instanceof Date 
          ? options.StartDateFrom.toISOString() 
          : options.StartDateFrom;
      }
      
      if (options.StartDateTo) {
        params.StartDateTo = options.StartDateTo instanceof Date 
          ? options.StartDateTo.toISOString() 
          : options.StartDateTo;
      }
      
      if (options.CategoryIds && options.CategoryIds.length > 0) {
        params.CategoryIds = options.CategoryIds.join(',');
      }

      return await apiService.get(`${this.basePath}/performance-summary`, params);
    } catch (error) {
      console.error('Failed to fetch performance summary:', error);
      throw error;
    }
  }

  /**
   * Get performance trend data using the optimized endpoint
   * @param {Object} options - Filter options
   * @param {number} [options.ExamTypeId] - Optional exam type ID to filter by
   * @param {string} [options.ExamMode] - Optional exam mode to filter by (e.g., "Practice", "Simulated")
   * @param {Date} [options.StartDateFrom] - Optional start date range from
   * @param {Date} [options.StartDateTo] - Optional start date range to
   * @param {number[]} [options.CategoryIds] - Optional category IDs to filter by
   * @returns {Promise<Array>} - Array of PerformanceTrendItemModel
   */
  async getPerformanceTrend(options = {}) {
    try {
      const params = {};
      
      if (options.ExamTypeId) params.ExamTypeId = options.ExamTypeId;
      if (options.ExamMode) params.ExamMode = options.ExamMode;
      if (options.StartDateFrom) {
        params.StartDateFrom = options.StartDateFrom instanceof Date 
          ? options.StartDateFrom.toISOString() 
          : options.StartDateFrom;
      }
      if (options.StartDateTo) {
        params.StartDateTo = options.StartDateTo instanceof Date 
          ? options.StartDateTo.toISOString() 
          : options.StartDateTo;
      }
      if (options.CategoryIds && options.CategoryIds.length > 0) {
        params.CategoryIds = options.CategoryIds.join(',');
      }

      return await apiService.get(`${this.basePath}/performance-trend`, params);
    } catch (error) {
      console.error('Failed to fetch performance trend:', error);
      throw error;
    }
  }

  /**
   * Get daily performance data for the last N days
   * @param {number} days - Number of days to look back (default 7)
   * @param {number} [examTypeId] - Optional exam type ID filter
   * @returns {Promise<Array>} - Array of daily performance data
   */
  async getDailyPerformanceReport(days = 7, examTypeId = null) {
    try {
      const dailyData = [];
      const today = new Date();
      
      // Generate requests for each day
      const dailyPromises = [];
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        
        const DateFrom = new Date(date);
        DateFrom.setHours(0, 0, 0, 0);
        
        const DateTo = new Date(date);
        DateTo.setHours(23, 59, 59, 999);
        
        const options = { DateFrom, DateTo };
        if (examTypeId) {
          options.ExamTypeId = examTypeId;
        }
        
        dailyPromises.push(
          this.getTestByCategoryReport(options).then(data => ({
            date: DateFrom,
            data: data || []
          }))
        );
      }
      
      const results = await Promise.all(dailyPromises);
      
      // Process results into daily performance format
      const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      
      return results.map(({ date, data }) => {
        const totalQuestions = data.reduce((sum, item) => sum + (item.totalQuestions || 0), 0);
        const questionsAnswered = data.reduce((sum, item) => sum + (item.questionsAnswered || 0), 0);
        const completionPercentage = totalQuestions > 0 ? Math.round((questionsAnswered / totalQuestions) * 100) : 0;
        
        return {
          day: daysOfWeek[date.getDay()],
          date: date.toISOString().split('T')[0], // YYYY-MM-DD format
          totalQuestions,
          questionsAnswered,
          completionPercentage,
          categories: data
        };
      });
    } catch (error) {
      console.error('Failed to fetch daily performance report:', error);
      throw error;
    }
  }
}

const progressReportService = new ProgressReportService();
export default progressReportService;
